// Considered: DialogWrapper (the prior implementation's shell) — rejected, same defect the
// relationship sheet hit: `CustomDialog` portals to `document.body`, which puts every datum
// outside the rail's container and makes the parity walker judge the editor as uncontained.
// The contract's own shape is not a modal at all — the editor opens in the rail beside the
// rule's row (`side > rule > panel > group > ... > <BusinessRuleEditor>`), portaled there by
// the composer (`ruleEditPortalNode`, `data-container="side > rule"`), which is what this
// renders. Every datum here carries the `side.*` id the decomposition assigns this call site.
//
// Considered: keeping the prior name/severity/enabled/executeOn form and leaving the
// condition as a read-only sentence (the Phase 1c scope note this file used to carry) —
// rejected. The contract's editor IS the sentence: an If gate, a Then requirement, and the
// two trees that build them. The tree lives in `RuleConditionBuilderMolecule`, mounted twice
// here, once per half of the sentence.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'localization';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import type {
  BusinessRule,
  BusinessRuleBlockingBehavior,
  BusinessRuleCondition,
  BusinessRuleOperation,
  BusinessRuleSeverity,
} from 'types/businessRules';
import type { SchemaProperty } from 'types/schema';
import { propertyEligibility, ruleProblems, type RuleProblem } from '../../../utils/ruleProblems';
import RuleConditionBuilderMolecule, {
  type RuleConditionBuilderInitialState,
  type RuleConditionBuilderParity,
} from './RuleConditionBuilderMolecule';
import { RuleSentence } from './TypeRuleRowMolecule';
import InputAtom from '../../atoms/Input/InputAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import InlineConfirmMolecule from './InlineConfirmMolecule';
import './RuleSheetMolecule.scss';

const KEY = 'studio.typeEditor.businessRules';

/** Contract order (`BR_SEVERITY`), not the type union's. */
const SEVERITIES: BusinessRuleSeverity[] = ['error', 'warning', 'info'];
const BLOCKING: BusinessRuleBlockingBehavior[] = ['block', 'warn', 'log'];
const EXECUTE_ON: BusinessRuleOperation[] = ['create', 'update', 'flow_transition'];

/** `defaultBlocking` — the behaviour a severity implies when nothing overrides it. */
export const defaultBlocking = (severity: BusinessRuleSeverity): BusinessRuleBlockingBehavior =>
  severity === 'error' ? 'block' : severity === 'warning' ? 'warn' : 'log';

/** The vocabulary the engine has no operator for, stated with the workaround. */
const ABSENT = ['between', 'isEmpty', 'startsWith', 'inTheLast'] as const;

/**
 * The shapes people arrive wanting. A rule is an if/then sentence, and the fastest way to
 * say so is to hand the sentence over half-built — pick two properties and it is written.
 * Every one is expressible; none is obvious from an empty tree.
 */
const STARTERS: {
  id: string;
  make: (a: string, b: string) => Pick<BusinessRule, 'applyWhen' | 'condition'>;
}[] = [
  {
    id: 'valueThenSet',
    make: (a, b) => ({
      applyWhen: { operator: 'and', expressions: [{ property: a, operator: 'equals', value: '' }] },
      condition: { operator: 'and', expressions: [{ property: b, operator: 'exists' }] },
    }),
  },
  {
    id: 'valueThenOneOf',
    make: (a, b) => ({
      applyWhen: { operator: 'and', expressions: [{ property: a, operator: 'equals', value: '' }] },
      condition: { operator: 'and', expressions: [{ property: b, operator: 'in', value: [] }] },
    }),
  },
  {
    id: 'notExceed',
    make: (a, b) => ({
      applyWhen: {
        operator: 'and',
        expressions: [
          { property: a, operator: 'exists' },
          { property: b, operator: 'exists' },
        ],
      },
      condition: {
        operator: 'and',
        expressions: [{ property: a, operator: 'less_than_or_equal', compareToProperty: b }],
      },
    }),
  },
  {
    id: 'bothSet',
    make: (a, b) => ({
      applyWhen: { operator: 'and', expressions: [{ property: a, operator: 'exists' }] },
      condition: { operator: 'and', expressions: [{ property: b, operator: 'exists' }] },
    }),
  },
  {
    id: 'mutuallyExclusive',
    make: (a, b) => ({
      applyWhen: undefined,
      condition: {
        operator: 'or',
        expressions: [
          { property: a, operator: 'is_false' },
          { property: b, operator: 'is_false' },
        ],
      },
    }),
  },
];

/**
 * The contract ids for the gate's tree (`<CSection> > <BRTree>`). The same tree is mounted
 * twice — once per half of the sentence — and the decomposition walks each mounting
 * separately, so each carries its own ids rather than sharing one map.
 */
const GATE_PARITY: RuleConditionBuilderParity = {
  lead: 'side.lead',
  all: 'side.all',
  any: 'side.any',
  ofTheFollowing: 'side.of-the-following',
  spacer: 'side.text#2',
  clear: 'side.clear',
  addCondition: 'side.add-condition',
  addGroup: 'side.add-group',
  group: {
    joiner: 'side.and',
    label: 'side.group',
    all: 'side.all#2',
    any: 'side.any#2',
    spacer: 'side.text#3',
    remove: 'side.x#3',
    add: 'side.add',
  },
  groupExpression: {
    joiner: 'side.joiner',
    property: 'side.choose-a-property',
    caret: 'side.x#4',
    operator: 'side.select',
    valueSummary: 'side.choose',
    valueCaret: 'side.x#5',
    remove: 'side.x#6',
    search: 'side.search-this-type-s-properties',
    searchClose: 'side.x#7',
    pickerItem: 'side.button',
    pickerItemLabel: 'side.x-label',
    pickerItemNote: 'side.ok-why',
    compareValue: 'side.a-value',
    compareProperty: 'side.another-property',
    comparePropertySelect: 'side.select#2',
    optionPill: 'side.string-v',
    listInput: 'side.input',
    valueInput: 'side.input#2',
  },
  expression: {
    joiner: 'side.joiner#2',
    property: 'side.choose-a-property#2',
    caret: 'side.x#8',
    operator: 'side.select#3',
    valueSummary: 'side.choose#2',
    valueCaret: 'side.x#9',
    remove: 'side.x#10',
    search: 'side.search-this-type-s-properties#2',
    searchClose: 'side.x#11',
    pickerItem: 'side.button#2',
    pickerItemLabel: 'side.x-label#2',
    pickerItemNote: 'side.ok-why#2',
    compareValue: 'side.a-value#2',
    compareProperty: 'side.another-property#2',
    comparePropertySelect: 'side.select#4',
    optionPill: 'side.string-v#2',
    listInput: 'side.input#3',
    valueInput: 'side.input#4',
  },
};

/** The requirement's tree (`<CSection>#2 > <BRTree>`) — the gate's map, one mounting later. */
const REQUIREMENT_PARITY: RuleConditionBuilderParity = {
  lead: 'side.lead#2',
  all: 'side.all#3',
  any: 'side.any#3',
  ofTheFollowing: 'side.of-the-following#2',
  spacer: 'side.text#4',
  clear: 'side.clear#2',
  addCondition: 'side.add-condition#2',
  addGroup: 'side.add-group#2',
  group: {
    joiner: 'side.and#2',
    label: 'side.group#2',
    all: 'side.all#4',
    any: 'side.any#4',
    spacer: 'side.text#5',
    remove: 'side.x#12',
    add: 'side.add#2',
  },
  groupExpression: {
    joiner: 'side.joiner#3',
    property: 'side.choose-a-property#3',
    caret: 'side.x#13',
    operator: 'side.select#5',
    valueSummary: 'side.choose#3',
    valueCaret: 'side.x#14',
    remove: 'side.x#15',
    search: 'side.search-this-type-s-properties#3',
    searchClose: 'side.x#16',
    pickerItem: 'side.button#4',
    pickerItemLabel: 'side.x-label#3',
    pickerItemNote: 'side.ok-why#3',
    compareValue: 'side.a-value#3',
    compareProperty: 'side.another-property#3',
    comparePropertySelect: 'side.select#6',
    optionPill: 'side.string-v#3',
    listInput: 'side.input#5',
    valueInput: 'side.input#6',
  },
  expression: {
    joiner: 'side.joiner#4',
    property: 'side.choose-a-property#4',
    caret: 'side.x#17',
    operator: 'side.select#7',
    valueSummary: 'side.choose#4',
    valueCaret: 'side.x#18',
    remove: 'side.x#19',
    search: 'side.search-this-type-s-properties#4',
    searchClose: 'side.x#20',
    pickerItem: 'side.button#5',
    pickerItemLabel: 'side.x-label#4',
    pickerItemNote: 'side.ok-why#4',
    compareValue: 'side.a-value#4',
    compareProperty: 'side.another-property#4',
    comparePropertySelect: 'side.select#8',
    optionPill: 'side.string-v#4',
    listInput: 'side.input#7',
    valueInput: 'side.input#8',
  },
};

/** The subset of a rule this editor authors. */
export type RuleSheetPatch = Pick<
  BusinessRule,
  | 'name'
  | 'description'
  | 'severity'
  | 'enabled'
  | 'executeOn'
  | 'applyWhen'
  | 'condition'
  | 'blockingBehavior'
  | 'flowStage'
>;

export interface RuleSheetMoleculeProps {
  visible: boolean;
  /** `null` = authoring a brand-new rule; otherwise the row being edited. */
  rule: BusinessRule | null;
  /** Everything this type holds — what the condition trees can be about. */
  properties?: SchemaProperty[];
  /** An inherited rule is read here, never edited: it belongs to the type that declared it. */
  readOnly?: boolean;
  /** The ancestor type that declared this rule, when it is inherited. */
  inheritedFromName?: string;
  /** The arc stages a `flow_transition` rule can be pinned to. Empty when the type has no arc. */
  arcStages?: string[];
  onHide: () => void;
  onSave: (patch: RuleSheetPatch) => void;
  /** Removes the rule outright. Omitted for a read-only editor. */
  onRemove?: () => void;
  /** Seeds one of the trees' interaction states so a capture can BE that reading. */
  initialGateState?: RuleConditionBuilderInitialState;
  initialRequirementState?: RuleConditionBuilderInitialState;
  className?: string;
}

const EMPTY_CONDITION: BusinessRuleCondition = { operator: 'and', expressions: [] };

function draftOf(rule: BusinessRule | null): RuleSheetPatch {
  return {
    name: rule?.name ?? '',
    description: rule?.description ?? '',
    severity: rule?.severity ?? 'error',
    enabled: rule?.enabled ?? true,
    executeOn: rule?.executeOn ?? ['create', 'update'],
    applyWhen: rule?.applyWhen,
    condition: rule?.condition ?? EMPTY_CONDITION,
    blockingBehavior: rule?.blockingBehavior,
    flowStage: rule?.flowStage,
  };
}

const RuleSheetMolecule: React.FC<RuleSheetMoleculeProps> = ({
  visible,
  rule,
  properties = [],
  readOnly = false,
  inheritedFromName,
  arcStages = [],
  onHide,
  onSave,
  onRemove,
  initialGateState,
  initialRequirementState,
  className,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<RuleSheetPatch>(() => draftOf(rule));
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    setDraft(draftOf(rule));
    setConfirmingDelete(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, rule?.id]);

  const usable = useMemo(
    () => properties.filter((property) => propertyEligibility(property).ok),
    [properties]
  );

  const asRule = useMemo<BusinessRule>(
    () => ({ ...(rule ?? ({ id: 'new', messageKey: '' } as BusinessRule)), ...draft }),
    [rule, draft]
  );
  const problems = useMemo(() => ruleProblems(asRule, properties), [asRule, properties]);
  const blocking = problems.filter((problem) => problem.blocks);
  const advisory = problems.filter((problem) => !problem.blocks);
  /**
   * The guard the engine's relational quirk demands, offered as one click rather than as a
   * paragraph the author has to translate back into two expressions.
   */
  const needsGuard = advisory.find((problem) => problem.key === 'rejectsUnsetSides');

  const sheetRef = useRef<HTMLDivElement>(null);
  // The editor opens in place beneath its row, so it is not modal: Tab still
  // reaches the rest of the page, and Escape is only its own when raised inside.
  useDialogFocus(visible, sheetRef, { onDismiss: onHide, trapFocus: false });

  if (!visible) return null;

  const set = (patch: Partial<RuleSheetPatch>) => setDraft((prev) => ({ ...prev, ...patch }));
  const gateWritten = (draft.applyWhen?.expressions ?? []).length > 0 || !!draft.applyWhen;
  const problemText = (problem: RuleProblem) =>
    t(`${KEY}.problem.${problem.key}`, {
      ...problem.params,
      where:
        problem.params?.where === 'requires'
          ? t(`${KEY}.problem.whereRequires`)
          : t(`${KEY}.problem.whereOnlyWhen`),
      why: problem.params?.whyKey ? t(`${KEY}.eligibility.${problem.params.whyKey}`) : '',
      operator: problem.params?.operator
        ? t(`${KEY}.operator.${problem.params.operator}`)
        : undefined,
      other: problem.params?.other ?? t(`${KEY}.aValue`).toLowerCase(),
    });

  const addGuard = () => {
    const names: string[] = [];
    (draft.condition?.expressions ?? []).forEach((entry) => {
      if ('expressions' in entry) return;
      if (!names.includes(entry.property)) names.push(entry.property);
      if (entry.compareToProperty && !names.includes(entry.compareToProperty)) {
        names.push(entry.compareToProperty);
      }
    });
    const have = draft.applyWhen?.expressions ?? [];
    set({
      applyWhen: {
        operator: 'and',
        expressions: [
          ...have,
          ...names
            .filter(
              (name) =>
                !have.some(
                  (entry) =>
                    !('expressions' in entry) &&
                    entry.property === name &&
                    entry.operator === 'exists'
                )
            )
            .map((name) => ({ property: name, operator: 'exists' as const })),
        ],
      },
    });
  };

  /** `<CSection>` — a titled band with an optional note, the contract's own grouping. */
  const section = (
    headingDatum: string,
    label: string,
    note: string | undefined,
    children: React.ReactNode,
    testId: string
  ) => (
    <div className="rule-sheet__section" data-testid={testId}>
      <div className="rule-sheet__section-head">
        <h3 data-datum={headingDatum} data-kind="heading">
          {label}
        </h3>
        {note && <i>{note}</i>}
      </div>
      {children}
    </div>
  );

  const severityNote = t(`${KEY}.severityNote.${draft.severity}`);
  const blockingValue = draft.blockingBehavior ?? defaultBlocking(draft.severity);
  const runsOnTransition = (draft.executeOn ?? []).includes('flow_transition');

  return (
    <div
      className={['rule-sheet', className].filter(Boolean).join(' ')}
      data-testid="rule-sheet"
      ref={sheetRef}
      role="group"
      aria-label={draft.name || t(`${KEY}.untitledRule`)}
    >
      {inheritedFromName && (
        <div className="rule-sheet__inherited" data-testid="rule-sheet-inherited">
          <span>
            <b data-datum="side.wrote-this-rule" data-kind="strong">
              {t(`${KEY}.wroteThisRule`, { name: inheritedFromName })}
            </b>{' '}
            {t(`${KEY}.inheritedHolds`)}
          </span>
        </div>
      )}

      <div className="rule-sheet__head">
        <h2 className="rule-sheet__title" data-datum="side.untitled-rule#2" data-kind="heading">
          {readOnly ? (
            <b data-datum="side.untitled-rule#3" data-kind="strong">
              {draft.name || t(`${KEY}.untitledRule`)}
            </b>
          ) : (
            <InputAtom
              className="rule-sheet__name"
              value={draft.name}
              placeholder={t(`${KEY}.namePlaceholder`)}
              aria-label={t(`${KEY}.nameAria`)}
              data-datum="side.rule-name"
              data-kind="input"
              data-testid="rule-sheet-name"
              onChange={(event) => set({ name: event.target.value })}
            />
          )}
        </h2>
        <span
          className="rule-sheet__hint"
          data-datum="side.what-must-stay-true-not-what-to-do-about"
          data-kind="hint"
        >
          {t(`${KEY}.headHint`)}
        </span>
        <button
          type="button"
          className="rule-sheet__quiet"
          data-datum="side.done"
          data-kind="button"
          data-testid="rule-sheet-done"
          onClick={onHide}
        >
          {t(`${KEY}.done`)}
        </button>
      </div>

      <div className="rule-sheet__readback" data-datum="side.text" data-kind="text">
        <span data-datum="side.x#2" data-kind="hint">
          <RuleSentence
            rule={asRule}
            ifDatum="side.if#2"
            thenDatum="side.then#2"
            alwaysDatum="side.always#2"
          />
        </span>
      </div>

      {/*
        The sentence in the order it is spoken. The gate is not an optional extra tucked
        behind the requirement — for most rules it IS the rule, and leading with the
        requirement hides the shape most authors came here to write.
      */}
      {section(
        'side.label',
        t(`${KEY}.sectionIf`),
        t(`${KEY}.sectionIfNote`),
        gateWritten ? (
          <>
            <RuleConditionBuilderMolecule
              value={draft.applyWhen}
              onChange={(next) => set({ applyWhen: next })}
              properties={usable}
              readOnly={readOnly}
              lead={t(`${KEY}.gateLead`)}
              emptyNote={t(`${KEY}.gateEmpty`)}
              parity={GATE_PARITY}
              initialState={initialGateState}
              testId="rule-sheet-gate"
            />
            <p
              className="rule-sheet__note"
              data-datum="side.while-this-doesn-u2019t-hold-the-rule-is"
              data-kind="note"
            >
              {t(`${KEY}.gateSkippedNote`)}
            </p>
            {!readOnly && (
              <button
                type="button"
                className="rule-sheet__quiet"
                data-datum="side.always-check-it-instead"
                data-kind="button"
                data-testid="rule-sheet-always-instead"
                onClick={() => set({ applyWhen: undefined })}
              >
                {t(`${KEY}.alwaysCheckInstead`)}
              </button>
            )}
          </>
        ) : (
          <div
            className="rule-sheet__wiresum"
            data-datum="side.the-rule-is-checked-every-time-it-runs-o"
            data-kind="text"
            data-testid="rule-sheet-always"
          >
            <span
              data-datum="side.the-rule-is-checked-every-time-it-runs-o#2"
              data-kind="label"
            >
              <b data-datum="side.always#3" data-kind="strong">
                {t(`${KEY}.alwaysStrong`)}
              </b>{' '}
              {t(`${KEY}.alwaysExplained`)}
            </span>
            {!readOnly && (
              <button
                type="button"
                className="rule-sheet__quiet"
                data-datum="side.only-in-certain-cases-u2026"
                data-kind="button"
                data-testid="rule-sheet-only-in-certain-cases"
                onClick={() =>
                  set({
                    applyWhen: {
                      operator: 'and',
                      expressions: [
                        { property: usable[0]?.name ?? '', operator: 'equals', value: '' },
                      ],
                    },
                  })
                }
              >
                {t(`${KEY}.onlyInCertainCases`)}
              </button>
            )}
          </div>
        ),
        'rule-sheet-if'
      )}

      {section(
        'side.label#2',
        t(`${KEY}.sectionThen`),
        t(`${KEY}.sectionThenNote`),
        <>
          {!(draft.condition?.expressions ?? []).length && !readOnly && (
            <div className="rule-sheet__starters" data-testid="rule-sheet-starters">
              <div
                className="rule-sheet__starters-head"
                data-datum="side.start-from-a-shape"
                data-kind="heading"
              >
                {t(`${KEY}.startFromAShape`)}
              </div>
              {STARTERS.map((starter, index) => (
                <button
                  type="button"
                  key={starter.id}
                  className="rule-sheet__starter"
                  data-container="each(starters)"
                  data-testid={`rule-sheet-starter-${starter.id}`}
                  {...(index === 0 ? { 'data-datum': 'side.button#3', 'data-kind': 'button' } : {})}
                  onClick={() =>
                    set(starter.make(usable[0]?.name ?? '', (usable[1] ?? usable[0])?.name ?? ''))
                  }
                >
                  <span className="rule-sheet__starter-main">
                    <b data-datum="side.st-label" data-kind="strong">
                      {t(`${KEY}.starter.${starter.id}.label`)}
                    </b>
                    <i>{t(`${KEY}.starter.${starter.id}.say`)}</i>
                  </span>
                </button>
              ))}
            </div>
          )}
          <RuleConditionBuilderMolecule
            value={draft.condition}
            onChange={(next) => set({ condition: next })}
            properties={usable}
            readOnly={readOnly}
            lead={t(`${KEY}.requirementLead`)}
            emptyNote={t(`${KEY}.requirementEmpty`)}
            parity={REQUIREMENT_PARITY}
            initialState={initialRequirementState}
            testId="rule-sheet-requirement"
          />
          <p
            className="rule-sheet__note"
            data-datum="side.write-what-must-hold-not-the-thing-you-u"
            data-kind="note"
          >
            {t(`${KEY}.writeWhatMustHold`)}
          </p>
        </>,
        'rule-sheet-then'
      )}

      {section(
        'side.label#3',
        t(`${KEY}.sectionHowStrict`),
        undefined,
        <>
          <div className="rule-sheet__row">
            <span className="rule-sheet__label" data-datum="side.severity" data-kind="label">
              {t(`${KEY}.severityLabel`)}
            </span>
            <div className="rule-sheet__seg" role="radiogroup" aria-label={t(`${KEY}.severityAria`)}>
              {SEVERITIES.map((severity) => (
                <button
                  type="button"
                  key={severity}
                  role="radio"
                  aria-checked={draft.severity === severity}
                  data-container="each(options)"
                  data-on={draft.severity === severity ? '1' : undefined}
                  disabled={readOnly}
                  data-datum="side.o-label"
                  data-kind="button"
                  data-testid={`rule-sheet-severity-${severity}`}
                  onClick={() => set({ severity })}
                >
                  {t(`${KEY}.severity.${severity}`)}
                </button>
              ))}
            </div>
            <span
              className="rule-sheet__hint"
              data-datum="side.br-severity-find-s-s-id-r-severity-note"
              data-kind="hint"
            >
              {severityNote}
            </span>
          </div>
          {draft.severity !== 'error' && (
            <p
              className="rule-sheet__note"
              data-datum="side.warning-and-advisory-behave-identically-"
              data-kind="note"
            >
              {t(`${KEY}.warningAdvisoryNote`)}
            </p>
          )}
          <div className="rule-sheet__row">
            <span className="rule-sheet__label" data-datum="side.when-it-fires" data-kind="label">
              {t(`${KEY}.whenItFires`)}
            </span>
            <div className="rule-sheet__seg" role="radiogroup" aria-label={t(`${KEY}.whenItFires`)}>
              {BLOCKING.map((behavior) => (
                <button
                  type="button"
                  key={behavior}
                  role="radio"
                  aria-checked={blockingValue === behavior}
                  data-container="each(options)"
                  data-on={blockingValue === behavior ? '1' : undefined}
                  disabled={readOnly}
                  data-datum="side.o-label#2"
                  data-kind="button"
                  data-testid={`rule-sheet-blocking-${behavior}`}
                  onClick={() =>
                    set({
                      blockingBehavior:
                        behavior === defaultBlocking(draft.severity) ? undefined : behavior,
                    })
                  }
                >
                  {t(`${KEY}.blocking.${behavior}`)}
                </button>
              ))}
            </div>
            <span
              className="rule-sheet__hint"
              data-datum="side.overriding-the-default-for-this-severity"
              data-kind="hint"
            >
              {t(draft.blockingBehavior ? `${KEY}.overridingDefault` : `${KEY}.followingDefault`)}
            </span>
          </div>
        </>,
        'rule-sheet-how-strict'
      )}

      {section(
        'side.label#4',
        t(`${KEY}.sectionWhenItRuns`),
        undefined,
        <>
          <div className="rule-sheet__places" data-container="choice-group">
            {EXECUTE_ON.map((operation) => {
              const on = (draft.executeOn ?? []).includes(operation);
              return (
                <button
                  type="button"
                  key={operation}
                  role="checkbox"
                  aria-checked={on}
                  className="rule-sheet__place"
                  data-container="choice-group > each(executeOn)"
                  data-on={on ? '1' : undefined}
                  disabled={readOnly}
                  data-datum="side.choice-card"
                  data-kind="choice-card"
                  data-testid={`rule-sheet-execute-on-${operation}`}
                  onClick={() =>
                    set({
                      executeOn: on
                        ? (draft.executeOn ?? []).filter((entry) => entry !== operation)
                        : [...(draft.executeOn ?? []), operation],
                    })
                  }
                >
                  <span className="rule-sheet__place-top">
                    <span
                      className="rule-sheet__radio"
                      data-datum="side.radio-indicator"
                      data-kind="radio-indicator"
                      aria-hidden="true"
                    />
                    <b data-datum="side.x-label#5" data-kind="strong">
                      {t(`${KEY}.executeOn.${operation}`)}
                    </b>
                  </span>
                  <i>{t(`${KEY}.executeOnNote.${operation}`)}</i>
                </button>
              );
            })}
          </div>
          {runsOnTransition && (
            <>
              <div className="rule-sheet__row">
                <span className="rule-sheet__label" data-datum="side.stage" data-kind="label">
                  {t(`${KEY}.stage`)}
                </span>
                <DropdownAtom
                  className="rule-sheet__select"
                  dropdownSize="small"
                  value={draft.flowStage ?? ''}
                  disabled={readOnly}
                  aria-label={t(`${KEY}.stage`)}
                  data-datum="side.select#9"
                  data-kind="select"
                  data-testid="rule-sheet-stage"
                  optionLabel="label"
                  optionValue="value"
                  options={[
                    { label: t(`${KEY}.everyTransition`), value: '' },
                    ...arcStages.map((stage) => ({ label: stage, value: stage })),
                  ]}
                  onChange={(event) => set({ flowStage: (event.value as string) || undefined })}
                />
                <span
                  className="rule-sheet__hint"
                  data-datum="side.a-stage-only-applies-to-arc-transitions"
                  data-kind="hint"
                >
                  {t(`${KEY}.stageHint`)}
                </span>
              </div>
              <div className="rule-sheet__note" data-tone="warn" data-testid="rule-sheet-arc-warning">
                <span>
                  <b
                    data-datum="side.arc-transitions-don-t-reach-the-checker-"
                    data-kind="strong"
                  >
                    {t(`${KEY}.arcTransitionsNotReached`)}
                  </b>{' '}
                  <Trans i18nKey={`${KEY}.arcTransitionsBody`} components={{ u: <i /> }} />
                </span>
              </div>
            </>
          )}
        </>,
        'rule-sheet-when-it-runs'
      )}

      {section(
        'side.label#5',
        t(`${KEY}.sectionWhatItSays`),
        t(`${KEY}.sectionWhatItSaysNote`),
        <>
          <div className="rule-sheet__row">
            <label
              className="rule-sheet__label"
              htmlFor="rule-sheet-description"
              data-datum="side.description"
              data-kind="label"
            >
              {t(`${KEY}.descriptionLabel`)}
            </label>
            <InputAtom
              id="rule-sheet-description"
              className="rule-sheet__input"
              value={draft.description ?? ''}
              readOnly={readOnly}
              data-datum="side.input#9"
              data-kind="input"
              data-testid="rule-sheet-description"
              onChange={(event) => set({ description: event.target.value })}
            />
            <span
              className="rule-sheet__hint"
              data-datum="side.what-it-prevents-optional"
              data-kind="hint"
            >
              {t(`${KEY}.whatItPrevents`)}
            </span>
          </div>
          <p
            className="rule-sheet__note"
            data-datum="side.the-message-someone-sees-when-this-fails"
            data-kind="note"
          >
            {t(`${KEY}.messagePerLanguage`)}
          </p>
        </>,
        'rule-sheet-what-it-says'
      )}

      {!readOnly && confirmingDelete && (
        <InlineConfirmMolecule
          tone="danger"
          testId="rule-sheet-delete-confirm"
          datum="side.callout"
          titleId="rule-sheet-delete-confirm-title"
          title={t(`${KEY}.deleteConfirm.title`, { name: draft.name || t(`${KEY}.untitledRule`) })}
          titleTestId="rule-sheet-delete-confirm-title"
          body={t(`${KEY}.deleteConfirm.body`)}
          bodyTestId="rule-sheet-delete-confirm-body"
          actions={[
            {
              label: t(`${KEY}.deleteConfirm.accept`),
              onClick: () => onRemove?.(),
              variant: 'danger',
              'data-testid': 'rule-sheet-delete-confirm-accept',
            },
            {
              label: t(`${KEY}.deleteConfirm.keepIt`),
              onClick: () => setConfirmingDelete(false),
              variant: 'text',
              'data-testid': 'rule-sheet-delete-confirm-dismiss',
            },
          ]}
        />
      )}

      <div className="rule-sheet__foot" data-state={blocking.length ? 'open' : 'ready'}>
        {!readOnly && (
          <button
            type="button"
            className="rule-sheet__danger"
            data-datum="side.delete"
            data-kind="button"
            data-testid="rule-sheet-delete"
            onClick={() => setConfirmingDelete(true)}
          >
            {t(`${KEY}.deleteRule`)}
          </button>
        )}
        {!readOnly && (
          <span className="rule-sheet__state">
            {blocking.length ? (
              <>
                <b data-datum="side.to-fix#2" data-kind="strong">
                  {t(`${KEY}.toFix`, { count: blocking.length })}
                </b>
                <span>{problemText(blocking[0])}</span>
              </>
            ) : (
              <>
                <b data-datum="side.ready" data-kind="strong">
                  {t(`${KEY}.ready`)}
                </b>
                <span>
                  {t(draft.enabled === false ? `${KEY}.savedSwitchedOff` : `${KEY}.liveOnEverySphere`)}
                </span>
              </>
            )}
          </span>
        )}
        {!readOnly && (
          <button
            type="button"
            className="rule-sheet__button"
            data-datum="side.switch-on"
            data-kind="button"
            data-testid="rule-sheet-toggle-enabled"
            onClick={() => set({ enabled: draft.enabled === false })}
          >
            {t(draft.enabled === false ? `${KEY}.switchOn` : `${KEY}.switchOff`)}
          </button>
        )}
        {/* A held Apply states its reason rather than hiding it (ux-craft I2) — the
            count and the first problem sit beside it, above. */}
        <button
          type="button"
          className="rule-sheet__primary"
          disabled={blocking.length > 0}
          data-datum="side.apply"
          data-kind="button"
          data-testid="rule-sheet-apply"
          onClick={() => onSave(draft)}
        >
          {t(`${KEY}.apply`)}
        </button>
      </div>

      {(blocking.length > 0 || advisory.length > 0) && (
        <div className="rule-sheet__notes" data-testid="rule-sheet-problems">
          {blocking.map((problem, index) => (
            <div
              key={`b${index}`}
              className="rule-sheet__note"
              data-tone="grave"
            >
              <span>{problemText(problem)}</span>
            </div>
          ))}
          {advisory.map((problem, index) => (
            <div key={`a${index}`} className="rule-sheet__note" data-container="each(advisory)" data-tone="warn">
              <span>
                {problemText(problem)}
                {problem === needsGuard && !readOnly && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="rule-sheet__quiet"
                      data-datum="side.add-the-is-set-gate"
                      data-kind="button"
                      data-testid="rule-sheet-add-guard"
                      onClick={addGuard}
                    >
                      {t(`${KEY}.addIsSetGate`)}
                    </button>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="rule-sheet__wiresum" data-datum="side.x#21" data-kind="text">
        <span data-datum="side.x#22" data-kind="label">
          <b data-datum="side.not-available-here" data-kind="strong">
            {t(`${KEY}.notAvailableHere`)}
          </b>{' '}
          {ABSENT.map((entry, index) => (
            <span key={entry}>
              {index ? '; ' : ''}
              <i>{t(`${KEY}.absent.${entry}.word`)}</i> — {t(`${KEY}.absent.${entry}.alt`)}
            </span>
          ))}
          .
        </span>
      </div>
    </div>
  );
};

RuleSheetMolecule.displayName = 'RuleSheetMolecule';

export default RuleSheetMolecule;
